import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Lightweight list picker for Rask / Hurtigregistrering.
 * Matches design zip: compact fixed menu, search, primary + meta rows.
 */
export function QuickListPicker({
  title,
  items = [],
  loading = false,
  emptyLabel,
  searchPlaceholder,
  selectedId = null,
  onPick,
  onClose,
  top = 120,
  left = 40,
  width = 300,
  footer = null,
  showSearch = true,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus?.();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };
    const onDown = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (it) => {
      if (it?.isHeader) return false;
      if (!q) return true;
      const hay = `${it.primary || ""} ${it.secondary || ""} ${it.meta || ""}`.toLowerCase();
      return hay.includes(q);
    };

    const showGroups = items.some((it) => it.groupKey && it.groupKey !== "ungrouped");
    if (!showGroups) {
      return items.filter((it) => !it.isHeader && matches(it));
    }

    const out = [];
    const seenHeaders = new Set();
    for (const it of items) {
      if (it.isHeader) continue;
      if (!matches(it)) continue;
      if (it.groupKey && !seenHeaders.has(it.groupKey)) {
        seenHeaders.add(it.groupKey);
        out.push({
          id: `header-${it.groupKey}`,
          isHeader: true,
          primary: it.groupTitle || "",
        });
      }
      out.push(it);
    }
    return out;
  }, [items, query]);

  const maxTop = typeof window !== "undefined" ? window.innerHeight - 360 : top;
  const maxLeft = typeof window !== "undefined" ? window.innerWidth - width - 12 : left;
  const style = {
    top: Math.max(8, Math.min(top, maxTop)),
    left: Math.max(8, Math.min(left, maxLeft)),
    width,
  };

  return (
    <div className="hqe-picker-menu" style={style} ref={rootRef} role="listbox">
      {title ? <div className="hqe-popover-title">{title}</div> : null}
      {showSearch ? (
        <input
          ref={inputRef}
          className="hqe-picker-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder || t("hours.quick.picker_search")}
        />
      ) : null}
      <div className="hqe-picker-list">
        {loading ? (
          <div className="hqe-picker-empty">{t("hours.quick.loading")}</div>
        ) : !filtered.length ? (
          <div className="hqe-picker-empty">
            {emptyLabel || t("hours.quick.no_suggestions")}
          </div>
        ) : (
          filtered.map((it, index) => {
            if (it.isHeader) {
              return (
                <div key={it.id || `header-${index}`} className="hqe-picker-group-header" role="presentation">
                  {it.primary}
                </div>
              );
            }
            const active =
              selectedId != null && String(selectedId) === String(it.id);
            const inGrid = !!it.inGrid;
            return (
              <button
                key={String(it.id)}
                type="button"
                role="option"
                aria-selected={active}
                className={`hqe-picker-option${active ? " is-active" : ""}${
                  inGrid ? " is-in-grid" : ""
                }`}
                onClick={() => onPick?.(it)}
              >
                <span className="hqe-picker-option-text">
                  <span className="hqe-picker-option-primary">{it.primary}</span>
                  {it.secondary ? (
                    <span className="hqe-picker-option-secondary">
                      {it.secondary}
                    </span>
                  ) : null}
                </span>
                <span className="hqe-picker-option-meta">
                  {inGrid
                    ? t("hours.quick.tag_in_grid")
                    : it.meta || ""}
                </span>
              </button>
            );
          })
        )}
      </div>
      {footer}
      <div className="hqe-picker-footer">
        <button type="button" className="hqe-btn" onClick={onClose}>
          {t("hours.quick.cancel")}
        </button>
      </div>
    </div>
  );
}

export function activityPickerItem(row, { ungroupedLabel = "" } = {}) {
  const primary =
    row?.display_title ?? row?.displayTitle ?? row?.title ?? "";
  const number =
    row?.number != null && row.number !== "" ? String(row.number) : "";
  const groupId = row?.group_id ?? row?.group?.id ?? null;
  const groupKey = groupId == null ? "ungrouped" : `group-${groupId}`;
  return {
    id: row.id,
    primary: primary || number || String(row.id),
    secondary: null,
    meta: number && primary ? number : "",
    object: row,
    groupKey,
    groupTitle: groupId == null ? ungroupedLabel : row?.group?.title || "",
  };
}

export function projectPickerItem(project, { inGrid = false } = {}) {
  const title =
    project?.displayTitle ||
    project?.display_title ||
    project?.name ||
    [project?.project_number, project?.name].filter(Boolean).join(" ") ||
    String(project?.id);
  const meta =
    project?.customer?.displayTitle ||
    project?.customer?.name ||
    project?.status ||
    "";
  return {
    id: project.id,
    primary: title,
    secondary: meta,
    meta: "",
    inGrid,
    object: project,
  };
}
